import { createMemo } from "solid-js";
import type { useTheme } from "@/providers/theme-provider";
import { getGlowColors, getListContextIdForPath } from "../helpers";

interface AppThemeStateOptions {
  committedPath: () => string;
  theme: ReturnType<typeof useTheme>;
}

export function useAppThemeState(options: AppThemeStateOptions) {
  const listContextId = createMemo(() => getListContextIdForPath(options.committedPath()));

  const globalTheme = createMemo(
    () => options.theme.allThemes()?.find((r) => r.target_id === "global") ?? null,
  );

  const activeListTheme = createMemo(() => {
    const listId = listContextId();
    if (!listId) {
      return null;
    }
    const rawThemes = options.theme.allThemes();
    return rawThemes?.find((thm) => thm.target_id === listId) ?? null;
  });

  const activeBlur = createMemo(() => options.theme.isBlur());

  const activeListTokens = createMemo(() => {
    const listTheme = activeListTheme();
    if (!listTheme) {
      return null;
    }
    const parent = options.theme.tokens();
    // Mode is not passed: set by the single theme provider resolver. Previously
    // `systemIsDark()` was passed —the TEAM preference—, so with a
    // light theme and dark team preference these tokens came out dark.
    return options.theme.resolveScopedTokens(listTheme, {
      isBlurContext: activeBlur(),
      parentTokens: parent,
    });
  });

  const activeListBackground = createMemo(
    () => activeListTokens()?.background ?? options.theme.tokens()?.background,
  );

  const glows = createMemo(() =>
    getGlowColors(
      options.committedPath(),
      activeListTheme(),
      globalTheme(),
      options.theme.tokens(),
    ),
  );

  return {
    listContextId,
    activeBlur,
    activeListTokens,
    activeListBackground,
    glows,
  };
}
